import React, { useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, ShoppingBag, FileText, AlertTriangle, BarChart3,
  Wallet, ScrollText, Settings, Activity, LogOut, Menu, Plug, Search, KeyRound,
  Landmark, Users, UserPlus, Smartphone,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { useMutation } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import ThemeToggle from './ThemeToggle';
import LanguageToggle from './LanguageToggle';
import CommandPalette from './CommandPalette';
import QboFreezeBanner from './QboFreezeBanner';
import { cn } from '../lib/utils';
import { useEnvInfo } from '../hooks/useEnvInfo';

const navItems = (t, role) => [
  { to: '/dashboard', label: t('nav.dashboard'), icon: LayoutDashboard, testid: 'nav-dashboard' },
  { to: '/orders', label: t('nav.orders'), icon: ShoppingBag, testid: 'nav-orders' },
  // Fix 46 (2026-10-02): reporte de Clientes nuevos.
  { to: '/new-customers', label: t('nav.new_customers'), icon: UserPlus, testid: 'nav-new-customers' },
  // Fix 52 (2026-10-07): Reportes de ventas (todos los usuarios, como Clientes nuevos).
  { to: '/sales-reports', label: t('nav.sales_reports'), icon: BarChart3, testid: 'nav-sales-reports' },
  // Fix 51 (2026-10-06): Agenda iCloud (contactos de los iPhone), solo admin.
  ...(role === 'admin'
    ? [{ to: '/agenda', label: t('nav.agenda'), icon: Smartphone, testid: 'nav-agenda' }]
    : []),
  { to: '/settlements', label: t('nav.settlements'), icon: FileText, testid: 'nav-settlements' },
  { to: '/manual-review', label: t('nav.manual_review'), icon: AlertTriangle, testid: 'nav-manual-review' },
  ...((['admin', 'accountant'].includes(role))
    ? [{ to: '/duplicate-customers', label: t('nav.duplicate_customers'), icon: Users, testid: 'nav-duplicate-customers' }]
    : []),
  { to: '/clearing', label: t('nav.clearing'), icon: Wallet, testid: 'nav-clearing' },
  ...((['admin', 'accountant'].includes(role))
    ? [{ to: '/depositos', label: t('nav.depositos'), icon: Landmark, testid: 'nav-depositos' }]
    : []),
  { to: '/integrations', label: t('nav.integrations'), icon: Plug, testid: 'nav-integrations' },
  { to: '/logs', label: t('nav.logs'), icon: ScrollText, testid: 'nav-logs' },
  { to: '/config', label: t('nav.config'), icon: Settings, testid: 'nav-config' },
  { to: '/health', label: t('nav.health'), icon: Activity, testid: 'nav-health' },
];

function ChangePasswordDialog({ open, onOpenChange }) {
  const { t } = useLang();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const reset = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const mutation = useMutation({
    mutationFn: async () => (await api.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    })).data,
    onSuccess: () => {
      toast.success(t('auth.change_password_success'));
      reset();
      onOpenChange(false);
    },
    onError: (err) => {
      const status = err?.response?.status;
      toast.error(status === 401
        ? t('auth.change_password_wrong_current')
        : t('auth.change_password_error'));
    },
  });

  const mismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;
  const canSubmit = currentPassword && newPassword.length >= 8
    && newPassword === confirmPassword && !mutation.isPending;

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent data-testid="change-my-password-dialog">
        <DialogHeader>
          <DialogTitle>{t('auth.change_my_password')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>{t('auth.current_password')}</Label>
            <Input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              data-testid="change-password-current-input"
            />
          </div>
          <div>
            <Label>{t('auth.new_password')}</Label>
            <Input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              data-testid="change-password-new-input"
            />
          </div>
          <div>
            <Label>{t('auth.confirm_password')}</Label>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              data-testid="change-password-confirm-input"
            />
            {mismatch && (
              <p className="mt-1 text-[11px] text-[hsl(var(--status-danger))]" data-testid="change-password-mismatch-error">
                {t('auth.password_mismatch')}
              </p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={() => mutation.mutate()}
            disabled={!canSubmit}
            data-testid="change-password-submit-button"
          >
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SidebarContent({ onNavigate }) {
  const { t } = useLang();
  const { user, logout } = useAuth();
  const initials = (user?.email || 'U').slice(0, 2).toUpperCase();
  const [showChangePwd, setShowChangePwd] = useState(false);

  return (
    <div
      className="relative flex h-full flex-col overflow-hidden"
      style={{
        background: 'hsl(var(--sidebar))',
        boxShadow: 'inset -1px 0 0 var(--border-default)',
      }}
      data-testid="sidebar"
    >
      {/* Brand Header */}
      <div className="relative flex items-center gap-3 px-4 pt-5 pb-4" style={{ borderBottom: '1px solid var(--border-default)' }}>
        <div
          className="flex h-9 w-9 items-center justify-center rounded-[10px] overflow-hidden shrink-0"
          style={{
            background: 'hsl(var(--surface))',
            border: '1px solid rgba(212,168,67,0.30)',
            boxShadow: '0 0 0 3px rgba(212,168,67,0.08)',
          }}
        >
          <img
            src="/lovi-logo.png"
            alt="Lovi Creation"
            className="h-7 w-7 object-contain"
            data-testid="sidebar-logo"
          />
        </div>
        <div className="flex flex-col leading-tight min-w-0">
          <span className="text-[13px] font-semibold tracking-tight truncate" style={{ color: 'hsl(var(--sidebar-foreground))' }}>
            Lovi Creation
          </span>
          <span className="text-[11px] text-muted-foreground font-mono truncate">
            TikTok → QuickBooks
          </span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="relative flex-1 overflow-y-auto px-2 pt-3 pb-3 no-scrollbar">
        <div className="px-2 pb-1.5 mt-1">
          <span className="text-[9px] font-semibold uppercase tracking-[0.12em] text-muted-foreground/70">
            Navegación
          </span>
        </div>
        <div className="space-y-[1px]">
          {navItems(t, user?.role).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              onClick={() => onNavigate?.()}
              className={({ isActive }) =>
                cn(
                  'group relative flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] font-medium',
                  '[transition:all_140ms_cubic-bezier(0.16,1,0.3,1)]',
                  isActive
                    ? 'text-[#B88C32] pl-2'
                    : 'text-muted-foreground hover:bg-[var(--border-subtle)] hover:text-foreground'
                )
              }
              style={({ isActive }) =>
                isActive
                  ? {
                      background: 'linear-gradient(90deg, rgba(212,168,67,0.14) 0%, rgba(212,168,67,0.04) 100%)',
                      borderLeft: '2px solid #D4A843',
                      paddingLeft: '8px',
                      boxShadow: 'inset 0 1px 0 rgba(212,168,67,0.08)',
                    }
                  : undefined
              }
            >
              {({ isActive }) => (
                <>
                  <item.icon
                    className={cn(
                      'h-4 w-4 shrink-0 transition-colors',
                      isActive ? 'text-[#B88C32]' : 'text-muted-foreground/70 group-hover:text-foreground'
                    )}
                    strokeWidth={1.75}
                  />
                  <span>{item.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>

      {/* Footer (status + user) */}
      <div className="relative px-2 pt-2.5 pb-2.5" style={{ borderTop: '1px solid var(--border-default)' }}>
        {/* System status */}
        <div className="flex items-center gap-2 px-2.5 py-1.5 mb-1">
          <span
            className="relative inline-flex h-[7px] w-[7px] rounded-full"
            style={{
              background: '#34D399',
              boxShadow: '0 0 0 2px rgba(52,211,153,0.20)',
            }}
          >
            <span
              className="absolute inset-0 rounded-full bg-[#34D399] opacity-50"
              style={{ animation: 'pulseDot 2.5s ease infinite' }}
            />
          </span>
          <span className="text-[11px] text-muted-foreground flex-1">Sistema operativo</span>
          <span
            className="text-[10px] font-mono text-muted-foreground rounded-[6px] px-1.5 py-0.5"
            style={{ background: 'var(--border-subtle)' }}
          >
            v1.8
          </span>
        </div>

        {/* User menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left cursor-pointer hover:bg-[var(--border-subtle)]"
              style={{ transition: 'background 140ms ease' }}
              data-testid="sidebar-user-menu-trigger"
            >
              <div
                className="flex h-8 w-8 items-center justify-center rounded-full text-[11px] font-bold text-white shrink-0"
                style={{
                  background: 'linear-gradient(135deg, #D4A843, #B88C32)',
                  border: '1px solid rgba(212,168,67,0.30)',
                }}
              >
                {initials}
              </div>
              <div className="flex-1 min-w-0">
                <div className="truncate text-[12px] font-medium text-foreground">
                  {user?.full_name || user?.email}
                </div>
                <div className="text-[10px] uppercase tracking-wide font-semibold text-muted-foreground">
                  {user?.role}
                </div>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" side="top" className="w-56">
            <DropdownMenuItem
              onClick={() => setShowChangePwd(true)}
              data-testid="sidebar-change-password-item"
            >
              <KeyRound className="h-4 w-4 mr-2" /> {t('auth.change_my_password')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => logout()}
              data-testid="sidebar-logout-item"
              className="text-[hsl(var(--status-danger))]"
            >
              <LogOut className="h-4 w-4 mr-2" /> {t('nav.logout')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <ChangePasswordDialog open={showChangePwd} onOpenChange={setShowChangePwd} />
      </div>
    </div>
  );
}

export default function AppShell() {
  const { t } = useLang();
  const location = useLocation();
  const { envName, adapterMode, isProduction } = useEnvInfo();
  const titleKey = (() => {
    const seg = location.pathname.split('/').filter(Boolean)[0] || 'dashboard';
    return `nav.${seg.replace('-', '_')}`;
  })();
  return (
    <div className="flex min-h-screen bg-background">
      <aside className="hidden md:flex md:w-60 md:shrink-0">
        <SidebarContent />
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <QboFreezeBanner />
        <header
          className="sticky top-0 z-30 flex h-[52px] items-center justify-between glass px-4 sm:px-7"
          style={{ borderBottom: '1px solid var(--border-default)' }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="sm" className="md:hidden h-8 w-8 p-0" data-testid="mobile-menu-button">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent
                side="left"
                className="w-72 p-0"
                style={{ background: 'hsl(var(--sidebar))', color: 'hsl(var(--sidebar-foreground))', borderRight: '1px solid var(--border-default)' }}
              >
                <SidebarContent />
              </SheetContent>
            </Sheet>
            <Link to="/dashboard" className="flex items-center gap-2 min-w-0">
              <span className="truncate text-[14px] font-semibold tracking-tight text-foreground">
                {t(titleKey)}
              </span>
            </Link>
            <span
              className="hidden sm:inline-flex items-center rounded-full border px-2 py-[3px] text-[10px] font-mono font-bold uppercase tracking-[0.08em]"
              style={{
                borderColor: isProduction ? 'rgba(34,197,94,0.45)' : 'rgba(212,168,67,0.35)',
                color: isProduction ? '#22C55E' : 'hsl(var(--primary))',
                background: isProduction ? 'rgba(34,197,94,0.06)' : 'rgba(212,168,67,0.05)',
              }}
              data-testid="env-pill"
              title={`adapter=${adapterMode} · env=${envName}`}
            >
              {isProduction ? 'live' : 'demo'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => window.dispatchEvent(new Event('command-palette:open'))}
              className="hidden sm:inline-flex items-center gap-2 rounded-[10px] px-3 h-8 text-[12px] text-muted-foreground hover:text-foreground transition-colors"
              style={{
                background: 'var(--surface-2)',
                border: '1px solid var(--border-default)',
              }}
              data-testid="topbar-search-button"
              aria-label={t('search.open_palette')}
              title={t('search.open_palette')}
            >
              <Search className="h-[14px] w-[14px] opacity-60" strokeWidth={1.75} />
              <span className="hidden md:inline">{t('common.search')}</span>
              <span
                className="hidden md:inline-flex items-center justify-center rounded-md px-1.5 py-0.5 text-[10px] font-mono"
                style={{
                  background: 'var(--border-subtle)',
                  border: '1px solid var(--border-default)',
                  color: 'var(--text-muted, hsl(var(--muted-foreground)))',
                }}
              >
                ⌘K
              </span>
            </button>
            <LanguageToggle />
            <ThemeToggle />
          </div>
        </header>
        <main className="relative flex-1 px-6 py-7 sm:px-7 sm:py-8 lg:px-8">
          <div className="mx-auto w-full max-w-[1400px]">
            <Outlet />
          </div>
        </main>
      </div>
      <CommandPalette />
    </div>
  );
}
